import React, { useContext, useEffect, useState, useRef } from "react";
import "./Prompt.css";
import { useTranslationContext } from "../../contexts/TranslationContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import searchLight from "../../assets/images/searchLight.svg";
import searchDark from "../../assets/images/searchDark.svg";
import closeLight from "../../assets/images/closeLight.svg";
import closeDark from "../../assets/images/closeDark.svg";
import { fetchdropdownDetails, postFilteredResponse } from "../../store/apiActions";
import { useDispatch } from "react-redux";
import dropDownLogo from "../../assets/images/dropDownLogo.svg"
import dropDownDark from "../../assets/images/dropdownDark.svg"
import { env } from "../../environment.js";
import Dropdown from "../Dropdown/Dropdown";

function PromptExamplesModal({ isOpen, onClose, onSelectPrompt }) {
  const { translations, selectedLanguage } = useTranslationContext();
  const { isDarkMode } = useContext(ThemeContext);

  // Texts
  const promptExamplesText = translations["prompt_examples"] || "Prompt Examples";
  const areaText = translations["area"] || "Area";
  const categoryText = translations["category"] || "Category";
  const subcategoryText = translations["subcategory"] || "Subcategory";
  const searchPromptText = translations["search_prompt"] || "Search for a prompt";
  const noPromptText = translations["no_prompt"] || "No prompts available";

  const dispatch = useDispatch();

  // Dropdown data
  const [areas, setAreas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const modalRef = useRef(null);

  // Selected values
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [search, setSearch] = useState("");

  // Dropdown open states
  const [areaDropdownOpen, setAreaDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [subcategoryDropdownOpen, setSubcategoryDropdownOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  // 🔹 Fetch dropdown options
  const fetchDropdowns = async ({ area = "", category = "", subcategory = "" } = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        language: selectedLanguage || "English",
        ...(area && { area }),
        ...(category && { category }),
        ...(subcategory && { subcategory }),
      });

      const response = await dispatch(
        fetchdropdownDetails(`${env.baseURL}/get-sample-query-dropdown?${params}`)
      );

      if (response) {
        setAreas(response.area || []);
        setCategories(response.category || []);
        setSubcategories(response.subcategory || []);
      }
    } catch (err) {
      console.error("Error fetching dropdown data:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Fetch prompts
  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const normalizeValue = (val) => (val && val.trim() !== "" ? val : null);
      const requestBody = {
        language: selectedLanguage || "English",
        area: normalizeValue(selectedArea),
        category: normalizeValue(selectedCategory),
        subcategory: normalizeValue(selectedSubcategory),
        search_prompt: normalizeValue(search),
      };

      const response = await dispatch(
        postFilteredResponse(`${env.baseURL}/get-filtered-sample-query`, requestBody)
      );

      setPrompts(response?.prompts || response || []);
    } catch (err) {
      console.error("Error fetching prompts:", err);
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  };
  //Fetch dropdowns initially (language change or modal open)
  useEffect(() => {
    if (!isOpen) return;
    fetchDropdowns();
  }, [isOpen, selectedLanguage]);

  //Fetch prompts when dropdowns or language changes
  useEffect(() => {
    if (!isOpen) return;
    fetchPrompts();
  }, [isOpen, selectedArea, selectedCategory, selectedSubcategory, selectedLanguage, search.trim() !== ""]);

  //Debounced search
  useEffect(() => {
    if (!isOpen) return;
    const handler = setTimeout(() => {
      if (search.trim() !== "") {
        fetchPrompts();
      }
    }, 800);

    return () => clearTimeout(handler);
  }, [search, isOpen]);

  //Fetch dropdowns when filters change
  useEffect(() => {
    if (!isOpen) return;

    fetchDropdowns({
      area: selectedArea,
      category: selectedCategory,
      subcategory: selectedSubcategory,
    });
  }, [isOpen, selectedArea, selectedCategory, selectedSubcategory]);


  // Reset selections when language changes
  useEffect(() => {
    setSelectedArea("");
    setSelectedCategory("");
    setSelectedSubcategory("");
    setSearch("");
  }, [selectedLanguage]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="prompt-modal-overlay">
      <div className={`prompt-modal ${isDarkMode ? "dark-mode" : "light-mode"}`}ref={modalRef}>
        {/* Header */}
        <div className={`prompt-modal-header fw-semibold font-plus fs-16 ${isDarkMode ? "dark-mode" : "light-mode"}`}>
          {promptExamplesText}
          <button className="prompt-modal-close button-style fs-18" onClick={onClose}>
            <img src={isDarkMode ? closeDark : closeLight} alt="close-icon" className="close-icon" />
          </button>
        </div>

        {/* Filters */}
        <div className={`prompt-modal-filters ${isDarkMode ? "dark-mode" : "light-mode"}`}>
          <Dropdown
            label={areaText}
            items={areas}
            selectedItem={selectedArea || areaText}
            setSelectedItem={setSelectedArea}
            dropdownState={areaDropdownOpen}
            setDropdownState={setAreaDropdownOpen}
            showArrow={true}
            arrowIcon={isDarkMode ? dropDownDark : dropDownLogo}
            showClear={true}
          />
          <Dropdown
            label={categoryText}
            items={categories}
            selectedItem={selectedCategory || categoryText}
            setSelectedItem={setSelectedCategory}
            dropdownState={categoryDropdownOpen}
            setDropdownState={setCategoryDropdownOpen}
             showArrow={true}
            arrowIcon={isDarkMode ? dropDownDark : dropDownLogo}
            showClear={true}
          />
          <Dropdown
            label={subcategoryText}
            items={subcategories}
            selectedItem={selectedSubcategory || subcategoryText}
            setSelectedItem={setSelectedSubcategory}
            dropdownState={subcategoryDropdownOpen}
            setDropdownState={setSubcategoryDropdownOpen}
            showArrow={true}
            arrowIcon={isDarkMode ? dropDownDark : dropDownLogo}
            showClear={true}
          />

          {/* Search */}
          <div className={`search-box ${isDarkMode ? "dark-mode" : "light-mode"}`}>
            <img
              src={isDarkMode ? searchDark : searchLight}
              alt="search-icon"
              className={`search-icon ${isDarkMode ? "dark-mode" : "light-mode"}`}
            />
            <input
              type="text"
              placeholder={searchPromptText}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`search-input ${isDarkMode ? "dark-mode" : "light-mode"}`}
            />
          </div>
        </div>

        {/* Content */}
        <div className="prompt-modal-content">
          {loading ? (
            <div className="container-center">
              <div className="spinner"></div>
            </div>
          ) : prompts.length > 0 ? (
            <div className="prompt-grid">
              {prompts.map((prompt, idx) => (
                <div
                  key={prompt.id || idx}
                  className={`prompt-card ${isDarkMode ? "dark-mode" : "light-mode"}`}
                  onClick={() => {
                    onSelectPrompt?.(prompt.text);
                    onClose();
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <h3>{prompt.title}</h3>
                  <p>{prompt.text}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className={`no-prompt container-center fw-semibold fs-16 ${isDarkMode ? "dark-mode" : "light-mode"}`}>{noPromptText}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default PromptExamplesModal;
